import { useCallback, useEffect, useRef, useState } from "react";
import type { ColorTheme } from "../types";
import { setBranding } from "../branding";
import { SkeletonList } from "./Skeleton";
import { OrgDomainsPanel } from "./OrgDomainsPanel";

/**
 * Identidade do assistente para admin de ORG (não-sysadmin) - epico 1/9.
 * Edita o white-label da PRÓPRIA org (PUT /api/admin/org) + os textos de
 * system prompt do workspace ativo (PATCH /api/admin/workspaces/:id). Nunca
 * o branding global da instância (isso é /api/admin/branding, sysadmin only -
 * ver BrandingSettings).
 */
const COLOR_THEME_OPTIONS: { value: ColorTheme; label: string; swatch: string }[] = [
  { value: "laranja", label: "Laranja", swatch: "#c15f3c" },
  { value: "vermelho", label: "Vermelho", swatch: "#c0392b" },
  { value: "azul", label: "Azul", swatch: "#2f6fed" },
  { value: "verde", label: "Verde", swatch: "#2f9e5c" },
];

interface OrgBrandingForm {
  appName: string;
  companyName: string;
  ownerName: string;
  ownerRole: string;
  colorTheme: ColorTheme;
  logoDataUrl: string;
}

interface WorkspaceRow {
  id: string;
  name: string;
  workspaceLabel: string;
  customGuidance: string;
}

interface WorkspaceForm {
  workspaceLabel: string;
  customGuidance: string;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("Falha ao ler o arquivo."));
    reader.readAsDataURL(file);
  });
}

function wsToForm(ws: WorkspaceRow): WorkspaceForm {
  return {
    workspaceLabel: ws.workspaceLabel,
    customGuidance: ws.customGuidance,
  };
}

interface Props {
  /** Sysadmin só - edita a org escolhida no seletor em vez da própria (não reflete no chrome da própria sessão). */
  orgId?: string;
}

export function OrgIdentityPanel({ orgId }: Props) {
  const qs = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";
  const [orgName, setOrgName] = useState("");
  const [brandingForm, setBrandingForm] = useState<OrgBrandingForm | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [activeWsId, setActiveWsId] = useState<string | null>(null);
  const [wsForm, setWsForm] = useState<WorkspaceForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingOrg, setSavingOrg] = useState(false);
  const [savingWs, setSavingWs] = useState(false);
  const [savedOrg, setSavedOrg] = useState(false);
  const [savedWs, setSavedWs] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [orgR, wsR] = await Promise.all([fetch(`/api/org${qs}`), fetch(`/api/workspaces${qs}`)]);
      if (!orgR.ok) throw new Error("Falha ao carregar a organização.");
      const org = await orgR.json();
      setOrgName(org.name ?? "");
      const b = org.branding ?? {};
      setBrandingForm({
        appName: b.appName ?? "",
        companyName: b.companyName ?? "",
        ownerName: b.ownerName ?? "",
        ownerRole: b.ownerRole ?? "",
        colorTheme: b.colorTheme ?? "laranja",
        logoDataUrl: b.logoDataUrl ?? "",
      });
      const items: WorkspaceRow[] = org.workspaces ?? [];
      setWorkspaces(items);
      let activeId: string | null = null;
      if (wsR.ok) activeId = (await wsR.json()).activeId ?? null;
      const chosen = items.find((w) => w.id === activeId) ?? items[0];
      if (chosen) {
        setActiveWsId(chosen.id);
        setWsForm(wsToForm(chosen));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao carregar a configuração.");
    } finally {
      setLoading(false);
    }
  }, [qs]);

  useEffect(() => {
    void load();
  }, [load]);

  const switchWs = (id: string) => {
    const ws = workspaces.find((w) => w.id === id);
    setActiveWsId(id);
    if (ws) setWsForm(wsToForm(ws));
    setSavedWs(false);
  };

  const setOrgField = (key: keyof OrgBrandingForm, value: string) => {
    setBrandingForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSavedOrg(false);
  };

  const onLogoPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLogoError(null);
    if (file.size > 1_500_000) {
      setLogoError("Imagem muito grande (máx. ~1.5MB) - use um PNG/SVG menor.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setOrgField("logoDataUrl", dataUrl);
    } catch {
      setLogoError("Falha ao ler o arquivo.");
    }
  };

  const saveOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandingForm || savingOrg) return;
    setSavingOrg(true);
    setError(null);
    setSavedOrg(false);
    try {
      const r = await fetch(`/api/admin/org${qs}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: orgName, branding: brandingForm }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error ?? "Falha ao salvar.");
      // só reflete no chrome (header/sidebar) desta aba quando é a PRÓPRIA org
      // da sessão ativa - editando a org de outro cliente (sysadmin), o chrome
      // de quem está editando não deve mudar.
      if (!orgId) setBranding(brandingForm);
      setSavedOrg(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao salvar.");
    } finally {
      setSavingOrg(false);
    }
  };

  const saveWs = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wsForm || !activeWsId || savingWs) return;
    setSavingWs(true);
    setError(null);
    setSavedWs(false);
    try {
      const body = {
        workspaceLabel: wsForm.workspaceLabel,
        customGuidance: wsForm.customGuidance,
      };
      const r = await fetch(`/api/admin/workspaces/${activeWsId}${qs}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error ?? "Falha ao salvar.");
      setSavedWs(true);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao salvar.");
    } finally {
      setSavingWs(false);
    }
  };

  if (loading || !brandingForm) {
    return (
      <div className="admin-block">
        <SkeletonList rows={6} />
      </div>
    );
  }

  return (
    <>
      <form className="admin-block" onSubmit={saveOrg}>
        <h2>Identidade da organização</h2>
        <p className="admin-sub">
          Nome, marca e dono desta organização (vale só para quem acessa por este tenant).
        </p>
        {error && <div className="auth-expired" style={{ marginBottom: 12 }}>{error}</div>}
        {savedOrg && !error && (
          <div className="admin-empty small" style={{ color: "var(--success, #2a9d5c)", marginBottom: 12 }}>
            Configuração salva.
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="oi-orgName" style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Nome da organização
          </label>
          <input
            id="oi-orgName"
            type="text"
            value={orgName}
            onChange={(e) => { setOrgName(e.target.value); setSavedOrg(false); }}
            style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
          />
        </div>

        {([
          { key: "appName", label: "Nome do app", placeholder: "Selbetti Assistant" },
          { key: "companyName", label: "Empresa / produto dono do workspace", placeholder: "Selbetti" },
          { key: "ownerName", label: "Nome de quem opera esta instância", placeholder: "Maike Carvalho" },
          { key: "ownerRole", label: "Papel do dono (como aparece no system prompt)", placeholder: "engenheira da Selbetti" },
        ] as const).map(({ key, label, placeholder }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label htmlFor={`oi-${key}`} style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              {label}
            </label>
            <input
              id={`oi-${key}`}
              type="text"
              value={brandingForm[key]}
              placeholder={placeholder}
              onChange={(e) => setOrgField(key, e.target.value)}
              style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
            />
          </div>
        ))}

        <div style={{ marginBottom: 14 }}>
          <label htmlFor="oi-colorTheme" style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Tema de cor (accent da UI)
          </label>
          <select
            id="oi-colorTheme"
            value={brandingForm.colorTheme}
            onChange={(e) => setOrgField("colorTheme", e.target.value)}
            style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
          >
            {COLOR_THEME_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            {COLOR_THEME_OPTIONS.map((opt) => (
              <span
                key={opt.value}
                title={opt.label}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: opt.swatch,
                  border: brandingForm.colorTheme === opt.value ? "2px solid var(--text)" : "1px solid var(--border)",
                  cursor: "pointer",
                }}
                onClick={() => setOrgField("colorTheme", opt.value)}
              />
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Logo (login e header, quando logado)
          </label>
          <p className="admin-user-meta" style={{ marginBottom: 4 }}>
            PNG ou SVG pequeno (até ~1.5MB). Vazio = herda o logo global.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {brandingForm.logoDataUrl ? (
              <img
                src={brandingForm.logoDataUrl}
                alt="Logo atual"
                style={{ height: 32, maxWidth: 160, objectFit: "contain", background: "var(--bg-elevated)", borderRadius: 6, padding: 2 }}
              />
            ) : (
              <span className="admin-user-meta">(herdando o logo global)</span>
            )}
            <button type="button" className="btn-reject" onClick={() => fileInputRef.current?.click()}>
              {brandingForm.logoDataUrl ? "Trocar logo" : "Enviar logo"}
            </button>
            {brandingForm.logoDataUrl && (
              <button type="button" className="btn-reject" onClick={() => setOrgField("logoDataUrl", "")}>
                Remover
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/svg+xml,image/jpeg,image/webp"
              style={{ display: "none" }}
              onChange={(e) => void onLogoPicked(e)}
            />
          </div>
          {logoError && <div className="auth-expired" style={{ marginTop: 6 }}>{logoError}</div>}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-approve" type="submit" disabled={savingOrg}>
            {savingOrg ? "Salvando…" : "Salvar"}
          </button>
          <button className="btn-reject" type="button" disabled={savingOrg} onClick={() => void load()}>
            Descartar alterações
          </button>
        </div>
      </form>

      <OrgDomainsPanel orgId={orgId} />

      {wsForm && activeWsId && (
        <form className="admin-block" onSubmit={saveWs}>
          <h2>Workspace</h2>
          <p className="admin-sub">
            Rótulo e orientações extras do workspace escolhido abaixo. A <strong>estrutura do
            workspace</strong> e as <strong>perguntas de exemplo</strong> (negócio, técnico, suporte e
            "Sobre") agora são geradas automaticamente a partir da documentação dos repositórios e
            rotacionam a cada dia - não precisam mais ser escritas à mão.
          </p>

          {workspaces.length > 1 && (
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="oi-ws" style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                Workspace
              </label>
              <select
                id="oi-ws"
                value={activeWsId}
                onChange={(e) => switchWs(e.target.value)}
                style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
              >
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          )}

          {savedWs && !error && (
            <div className="admin-empty small" style={{ color: "var(--success, #2a9d5c)", marginBottom: 12 }}>
              Configuração salva.
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="oi-wsLabel" style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Rótulo curto do workspace
            </label>
            <input
              id="oi-wsLabel"
              type="text"
              value={wsForm.workspaceLabel}
              placeholder="Selbetti"
              onChange={(e) => { setWsForm((p) => (p ? { ...p, workspaceLabel: e.target.value } : p)); setSavedWs(false); }}
              style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="oi-wsGuidance" style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Orientações extras (markdown, opcional)
            </label>
            <textarea
              id="oi-wsGuidance"
              value={wsForm.customGuidance}
              rows={4}
              onChange={(e) => { setWsForm((p) => (p ? { ...p, customGuidance: e.target.value } : p)); setSavedWs(false); }}
              style={{ width: "100%", padding: "6px 8px", fontSize: 13, fontFamily: "monospace" }}
            />
          </div>

          <p className="admin-user-meta" style={{ marginBottom: 14 }}>
            A estrutura do workspace e as perguntas de exemplo (negócio, técnico, suporte e "Sobre") são
            geradas e atualizadas automaticamente a partir da documentação dos repositórios deste workspace.
          </p>

          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-approve" type="submit" disabled={savingWs}>
              {savingWs ? "Salvando…" : "Salvar"}
            </button>
            <button className="btn-reject" type="button" disabled={savingWs} onClick={() => void load()}>
              Descartar alterações
            </button>
          </div>
        </form>
      )}
    </>
  );
}
